import { CURRENT_FORM_VARIABLE, PROTOCOL_STEP_TYPE } from 'features/stepByStepProtocol/utils/protocol/constants';
import { normalizeText } from 'features/stepByStepProtocol/utils/protocol/normalizeText';

export class ProtocolParseError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ProtocolParseError';
    }
}

const KNOWN_STEP_TYPES = new Set(Object.values(PROTOCOL_STEP_TYPE));
const CURRENT_FORM_REF = `\${${CURRENT_FORM_VARIABLE}}`;

const childElements = (parent, tagName) => Array.from(parent?.children ?? []).filter((element) => element.tagName === tagName);
const firstChild = (parent, tagName) => childElements(parent, tagName)[0] ?? null;
const attribute = (element, name) => element?.getAttribute(name)?.trim() || null;

const childText = (parent, tagName) => {
    const child = firstChild(parent, tagName);
    return child ? normalizeText(child.textContent) || null : null;
};

// Untitled `<instruction>` blocks are what always applies, titled ones are per case. An empty
// untitled block carries nothing and is dropped.
const parseInstructions = (parent) =>
    childElements(parent, 'instruction')
        .map((element) => ({ title: attribute(element, 'title'), text: normalizeText(element.textContent) }))
        .filter(({ title, text }) => title || text);

const parseCaseValues = (caseElement) => {
    const listed = [attribute(caseElement, 'value'), attribute(caseElement, 'values')].filter(Boolean);
    return [
        ...new Set(
            listed
                .flatMap((list) => list.split(','))
                .map((value) => value.trim())
                .filter(Boolean)
        ),
    ];
};

const parseCase = (caseElement) => {
    const values = parseCaseValues(caseElement);
    if (!values.length) throw new ProtocolParseError('A <case> has no value');

    const currentForm = childElements(caseElement, 'set').find((set) => attribute(set, 'name') === CURRENT_FORM_VARIABLE);
    const stepRefs = childElements(firstChild(caseElement, 'steps'), 'step').map((step) => attribute(step, 'ref'));

    return { values, formId: attribute(currentForm, 'value'), stepRefs };
};

const parseForms = (root) => {
    const forms = {};
    for (const formElement of childElements(firstChild(root, 'forms'), 'form')) {
        const id = attribute(formElement, 'id');
        if (!id) continue;
        if (forms[id]) throw new ProtocolParseError(`Form "${id}" is defined twice`);

        const fields = childElements(formElement, 'field')
            .map((field) => ({ name: attribute(field, 'name'), type: attribute(field, 'type'), required: attribute(field, 'required') === 'true' }))
            .filter(({ name }) => name);
        forms[id] = { id, title: attribute(formElement, 'title'), fields };
    }
    return forms;
};

// Returns the steps and, separately, the `<render formRef>` of each step: the form reference is only
// validated, it is not part of the definition.
const parseSteps = (root) => {
    const steps = {};
    const formRefs = {};
    for (const stepElement of childElements(firstChild(root, 'steps'), 'step')) {
        const id = attribute(stepElement, 'id');
        if (!id) continue;
        if (steps[id]) throw new ProtocolParseError(`Step "${id}" is defined twice`);

        const type = attribute(stepElement, 'type');
        steps[id] = {
            id,
            type: KNOWN_STEP_TYPES.has(type) ? type : PROTOCOL_STEP_TYPE.INSTRUCTION,
            title: attribute(stepElement, 'title'),
            intent: childText(stepElement, 'intent'),
            instructions: parseInstructions(stepElement),
        };
        const formRef = attribute(firstChild(stepElement, 'render'), 'formRef');
        if (formRef) formRefs[id] = formRef;
    }
    return { steps, formRefs };
};

const resolveFormRef = (formRef, caseFormId) => (formRef === CURRENT_FORM_REF ? caseFormId : formRef);

const validateFormRefs = (formRefs, forms) => {
    for (const [stepId, formRef] of Object.entries(formRefs)) {
        const isVariable = formRef.startsWith('${');
        if (isVariable && formRef !== CURRENT_FORM_REF) throw new ProtocolParseError(`Step "${stepId}" renders an unknown variable ${formRef}`);
        if (!isVariable && !forms[formRef]) throw new ProtocolParseError(`Step "${stepId}" renders form "${formRef}", which does not exist`);
    }
};

const validateCase = ({ formId, stepRefs }, steps, forms, formRefs) => {
    if (formId && !forms[formId]) throw new ProtocolParseError(`Case form "${formId}" does not exist`);

    const seen = new Set();
    for (const stepRef of stepRefs) {
        if (!stepRef) throw new ProtocolParseError('A case step has no ref');
        if (!steps[stepRef]) throw new ProtocolParseError(`Step "${stepRef}" does not exist`);
        // A section per step: the same step twice in one case would show (and toggle) as one section twice.
        if (seen.has(stepRef)) throw new ProtocolParseError(`Step "${stepRef}" is listed twice in a case`);
        seen.add(stepRef);
        const formRef = formRefs[stepRef];
        if (formRef && !forms[resolveFormRef(formRef, formId)]) {
            throw new ProtocolParseError(`Step "${stepRef}" renders ${formRef}, which is no form for this case`);
        }
    }
};

/**
 * Parses a protocol XML string into the normalized definition (FE plan 5.1):
 * `{ id, schemaVersion, name, direction, vehicleSwitch, forms, steps }`.
 *
 * Only the tags the FE knows are read; anything else is ignored. Throws `ProtocolParseError` for XML
 * that must not be shown: not well-formed, a root other than `<protocol>`, no `<vehicleSwitch>`, no
 * steps, a duplicated step or form id, a `<case>` without values, a step reference without a step, a step
 * listed twice in one case, or a form reference without a form.
 */
export function parseProtocolXml(xmlText) {
    if (typeof xmlText !== 'string' || !xmlText.trim()) throw new ProtocolParseError('Protocol XML is empty');

    const xmlDocument = new DOMParser().parseFromString(xmlText, 'application/xml');
    if (xmlDocument.getElementsByTagName('parsererror').length) throw new ProtocolParseError('Protocol XML is not well-formed');

    const root = xmlDocument.documentElement;
    if (root?.tagName !== 'protocol') throw new ProtocolParseError(`Unexpected root element <${root?.tagName}>`);

    const metadata = firstChild(root, 'metadata');
    const vehicleSwitchElement = firstChild(root, 'vehicleSwitch');
    if (!vehicleSwitchElement) throw new ProtocolParseError('Protocol has no <vehicleSwitch>');

    const forms = parseForms(root);
    const { steps, formRefs } = parseSteps(root);
    if (!Object.keys(steps).length) throw new ProtocolParseError('Protocol has no steps');
    validateFormRefs(formRefs, forms);

    const cases = childElements(vehicleSwitchElement, 'case').map(parseCase);
    cases.forEach((protocolCase) => validateCase(protocolCase, steps, forms, formRefs));

    const defaultElement = firstChild(vehicleSwitchElement, 'default');

    return {
        id: attribute(root, 'id'),
        schemaVersion: attribute(root, 'version'),
        name: childText(metadata, 'name'),
        direction: childText(firstChild(metadata, 'gate'), 'direction'),
        vehicleSwitch: {
            field: attribute(vehicleSwitchElement, 'field'),
            cases,
            default: defaultElement ? { intent: childText(defaultElement, 'intent'), instructions: parseInstructions(defaultElement) } : null,
        },
        forms,
        steps,
    };
}
